import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authKeys } from "@/features/auth/hooks";
import { usersApi } from "./api";
import type { ProfileInput, StaffUser, UpdateUserInput, UsersFilters } from "./types";

export const userKeys = {
  all: ["users"] as const,
  list: (filters: UsersFilters) => ["users", "list", filters] as const,
};

export function useUsers(filters: UsersFilters) {
  return useQuery({ queryKey: userKeys.list(filters), queryFn: () => usersApi.list(filters) });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: usersApi.create, onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }) });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ user, input }: { user: StaffUser; input: UpdateUserInput }) => usersApi.update(user.id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: usersApi.delete, onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }) });
}

export function useResetPassword() {
  return useMutation({ mutationFn: ({ id, password }: { id: string; password: string }) => usersApi.resetPassword(id, password) });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProfileInput) => usersApi.updateProfile(input),
    onSuccess: (user) => {
      queryClient.setQueryData(authKeys.me, user);
      queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

export function useChangePassword() {
  return useMutation({ mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => usersApi.changePassword(currentPassword, newPassword) });
}
